/**
 * AviTableRow — Ligne du tableau AVI (Validation / Accusé / Information).
 *
 * Spec iter173 (2026-02-27) — extrait de `pages/Avi.jsx` (renderAviRow)
 * pour respecter la règle des ~700 lignes. AUCUN changement de
 * comportement — pure réorganisation de code.
 *
 * Colonnes rendues (spec iter169 + iter170 + iter171 pt.4) :
 *   1. Réf.
 *   2. Date et heure (JJ/MM/AA · HH:MM)
 *   3. Objet
 *   4. Demandeur (avatar + nom)
 *   5. Décideurs / Réceptionnaires / Lecteurs (polymorphe)
 *   6. Témoins
 *   7. Priorité
 *   8. Lecture (pastilles empilées œil / œil-barré)
 *   9. Discussion (bouton rond vert WhatsApp)
 *  10. Fichiers (HoverCard aperçu)
 *  11. Actions (Info, Modifier, Supprimer)
 */

import UserAvatar from "@/components/ui/UserAvatar";
import { hasRight } from "@/components/crm/crmShared";
import { useNavigate } from "@/hooks/useDeskNavigate";
import { useEffect, useState } from "react";
import {
  Eye, EyeOff, MessageCircle, Info as InfoIcon, Pencil, Trash2, Paperclip, Bell, Timer,
} from "lucide-react";
import { toast } from "@/lib/toast";
import api from "@/lib/api";
import { avatarColorForSeed } from "@/lib/avatarColors";
import {
  HoverCard, HoverCardTrigger, HoverCardContent,
} from "@/components/ui/hover-card";
import AviInfoHovercard from "@/components/avi/AviInfoHovercard";
import { AviActivityHovercard } from "@/components/documents/ActivityHovercard";
import { formatActivityShort, activityPillStyle } from "@/lib/dates";

const PRIORITY_META = {
  immediat: { label: "Immédiat", dot: "#EF4444" },
  urgent:   { label: "Urgent",   dot: "#F97316" },
  moyen:    { label: "Moyen",    dot: "#F9BC26" },
  normal:   { label: "Normal",   dot: "#4ADE80" },
};

function MiniAvatar({ user, size = 22 }) {
  return <UserAvatar user={user} size={size} showPopover={false} />;
}


/**
 * ClotutingCountdown — spec iter256 (2026-03-01).
 *
 * Compte à rebours avant clôture automatique de l'AVI. Deux états :
 *   • ``_auto_close_at`` présent (tous ont lu + délai configuré) →
 *     format ``Jj Hh MMm SSs`` réactualisé toutes les 30 s.
 *     Couleur : vert > 12h, orange < 12h, rouge < 1h.
 *   • absent → « — » gris (attente que tous les destinataires lisent).
 */
function ClotutingCountdown({ dueAt, autoCloseHours }) {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    // Refresh toutes les 30 s (précis à la ~minute pour l'utilisateur).
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  if (!dueAt) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px]"
        style={{ color: "#6B7280" }}
        title={autoCloseHours ? `Clôture automatique ${autoCloseHours}h après lecture par tous les destinataires.` : ""}
        data-testid="avi-cloturing-idle"
      >
        <Timer size={12} strokeWidth={2} />
        <span>—</span>
      </span>
    );
  }
  const target = new Date(dueAt).getTime();
  const diff = target - now;
  if (diff <= 0) {
    return (
      <span
        className="inline-flex items-center gap-1 text-[11px] font-semibold"
        style={{ color: "#EF4444" }}
        title="Clôture imminente — sera passée en statut final à la prochaine passe scheduler."
        data-testid="avi-cloturing-elapsed"
      >
        <Timer size={12} strokeWidth={2.4} />
        <span>0s</span>
      </span>
    );
  }
  const totalSec = Math.floor(diff / 1000);
  const d = Math.floor(totalSec / 86400);
  const h = Math.floor((totalSec % 86400) / 3600);
  const m = Math.floor((totalSec % 3600) / 60);
  const s = totalSec % 60;
  const parts = [];
  if (d > 0) parts.push(`${d}j`);
  if (h > 0 || d > 0) parts.push(`${h}h`);
  if (d === 0) parts.push(`${m}m`);
  if (d === 0 && h === 0) parts.push(`${s}s`);
  const label = parts.join(" ");
  const hoursLeft = diff / 3_600_000;
  const color =
    hoursLeft < 1 ? "#EF4444"
    : hoursLeft < 12 ? "#F97316"
    : "#22C55E";
  return (
    <span
      className="inline-flex items-center gap-1 text-[11px] tabular-nums font-semibold"
      style={{ color }}
      title={`Clôture auto le ${new Date(dueAt).toLocaleString("fr-FR")}`}
      data-testid="avi-cloturing-active"
    >
      <Timer size={12} strokeWidth={2.4} />
      <span>{label}</span>
    </span>
  );
}


/**
 * Trombone + compteur pour la colonne « Fichiers ». HoverCard qui liste
 * les pièces jointes avec téléchargement direct. Extrait tel quel de
 * l'ancien `Avi.jsx` (AviAttachmentsPopover local).
 */
function AviAttachmentsPopover({ avi, count }) {
  const hasAny = (count || 0) > 0;
  const list = avi.attachments || [];
  const dl = async (att) => {
    try {
      const res = await api.get(`/avi/${avi.id}/attachments/${att.id}/download`, {
        responseType: "blob",
      });
      const blob = new Blob([res.data], { type: att.content_type || "application/octet-stream" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = att.filename || "attachment";
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch {
      toast.error("Échec du téléchargement");
    }
  };
  return (
    <HoverCard openDelay={150} closeDelay={80}>
      <HoverCardTrigger asChild>
        <span
          className="inline-flex items-center gap-1 tabular-nums select-none"
          style={{ color: hasAny ? "#EDEDED" : "#666" }}
          data-testid={`avi-attach-trigger-${avi.id}`}
        >
          <Paperclip size={14} />
          {count || 0}
        </span>
      </HoverCardTrigger>
      {hasAny && (
        <HoverCardContent
          align="center"
          side="top"
          className="p-3"
          style={{ width: 300, background: "#0F0F10", border: "1px solid #2A2A2A" }}
          data-testid={`avi-attach-popover-${avi.id}`}
        >
          <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
            Pièces jointes · {list.length}
          </div>
          <ul className="space-y-1">
            {list.map((att) => (
              <li key={att.id}>
                <button
                  type="button"
                  onClick={() => dl(att)}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-left text-[12px] transition-colors hover:bg-white/[0.06]"
                  style={{ color: "#EDEDED" }}
                  data-testid={`avi-attach-item-${att.id}`}
                >
                  <Paperclip size={12} className="shrink-0 text-muted-foreground" />
                  <span className="truncate flex-1" title={att.filename}>{att.filename}</span>
                </button>
              </li>
            ))}
          </ul>
          <div className="text-[10px] text-muted-foreground mt-2">
            Cliquez sur un fichier pour le télécharger.
          </div>
        </HoverCardContent>
      )}
    </HoverCard>
  );
}

export default function AviTableRow({
  it, subtype, meta, isAccuse, isInfo,
  usersById, projectsById = {}, user, canAdmin, aviScope = "own",
  showCloturing = false, autoCloseHours = null,
  onEdit, onDelete,
}) {
  const navigate = useNavigate();
  const pri = PRIORITY_META[it.priority] || {};
  const activeRole = isAccuse ? "receptionnaire" : isInfo ? "lecteur" : "decideur";
  const deciders = (it.participants || []).filter((p) => p.role === activeRole);
  const witnesses = (it.participants || []).filter((p) => p.role === "temoin");
  const demandeur = usersById[it.created_by];
  const isMine = it.created_by === user?.id;
  // iter200 — icônes uniquement avec le droit (avi.edit / avi.delete) dans la portée ; être demandeur ne donne rien.
  const inScope = aviScope === "all" || isMine;
  const hasEdit = inScope && hasRight(user, "avi.edit");
  const canEdit = hasEdit && it.status === "en_attente";
  const canDel = inScope && hasRight(user, "avi.delete");
  const unread = it.messages_unread || 0;
  const msgTotal = it.messages_total || 0;
  const decidedSeen = (p) => Boolean(p.decision) && p.decision !== "en_attente";
  const nSeen = deciders.filter(decidedSeen).length;
  const nTotal = deciders.length;
  const attCount = (it.attachments?.length) || 0;
  return (
    <tr
      key={it.id}
      className="border-t hover:bg-white/[0.02] cursor-pointer relative"
      style={{ borderColor: "#2A2A2A", boxShadow: `inset 3px 0 0 0 ${meta.color}` }}
      onClick={() => navigate(`/avi/${it.id}`)}
      data-testid={`avi-row-${it.id}`}
    >
      <td className="py-2.5 pl-5 pr-3 font-mono text-[12px]" style={{ color: meta.color }}>
        <span className="inline-flex items-center gap-1.5">
          <span>{it.reference}</span>
          {/* Spec iter199+iter200 (2026-02-28) — Cloche 🔔 sur la
              ligne Liste dont le rappel est < 24h. Pulsation douce
              (halo jaune expand/fade) quand < 1h. */}
          {it._reminder_soon && (
            <span
              className={`inline-flex items-center justify-center rounded-full flex-shrink-0${it._reminder_urgent ? " avi-bell-pulse" : ""}`}
              style={{
                width: 15, height: 15,
                background: "#F5C84222",
                border: "1px solid #F5C84266",
              }}
              title={it._reminder_due_at
                ? `Rappel prévu ${new Date(it._reminder_due_at).toLocaleString("fr-FR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" })}${it._reminder_urgent ? " (< 1h)" : ""}`
                : "Rappel < 24h"}
              data-testid={`avi-row-bell-${it.id}`}
              onClick={(e) => e.stopPropagation()}
            >
              <Bell size={8.5} strokeWidth={2.8} style={{ color: "#F5C842" }} />
            </span>
          )}
        </span>
      </td>
      <td className="py-2.5 px-3 text-[11.5px] text-muted-foreground whitespace-nowrap tabular-nums">
        {(() => {
          try {
            const d = new Date(it.created_at);
            const date = d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "2-digit" });
            const time = d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" });
            return `${date} · ${time}`;
          } catch { return "—"; }
        })()}
      </td>
      <td className="py-2.5 px-3 font-medium">{it.subject}</td>
      <td className="py-2.5 px-3 text-xs">
        <span className="inline-flex items-center gap-1.5">
          <MiniAvatar user={demandeur} size={22} />
          <span className="truncate max-w-[120px]">
            {isMine
              ? "Moi"
              : (demandeur
                  ? `${demandeur.first_name || ""} ${demandeur.last_name || ""}`.trim() || demandeur.email
                  : "—")}
          </span>
        </span>
      </td>
      {/* Spec iter253 (2026-03-01) — Colonne Siège / Projet, alignée sur
          Documents/Tâches. Insérée entre Demandeur et la colonne
          polymorphe des destinataires (Décideurs/Réceptionnaires/Lecteurs)
          pour respecter l'ordre demandé (avant les colonnes propres au
          sous-module). */}
      <td className="py-2.5 px-3 text-[11.5px]">
        {(() => {
          const p = it.project_id ? projectsById[it.project_id] : null;
          if (!p) return <span className="text-muted-foreground">—</span>;
          return (
            <span
              className="truncate inline-block max-w-[140px]"
              style={{ color: "#D1D5DB" }}
              title={p.name}
              data-testid={`avi-row-project-${it.id}`}
            >
              {p.code_label || p.name}
            </span>
          );
        })()}
      </td>
      <td className="py-2.5 px-3">
        <div className="flex items-center -space-x-1.5">
          {deciders.slice(0, 4).map((p) => (
            <MiniAvatar key={p.user_id} user={usersById[p.user_id]} size={22} />
          ))}
          {deciders.length > 4 && (
            <span className="ml-1 text-[10px] text-muted-foreground">+{deciders.length - 4}</span>
          )}
          {deciders.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
        </div>
      </td>
      {/* Spec iter180 pt.3 (2026-02-27) — colonne « Témoins » retirée sur
          Information (rôle Témoin supprimé du sous-type). Conservée sur
          Validation et Accusé où le rôle reste actif. */}
      {!isInfo && (
        <td className="py-2.5 px-3">
          <div className="flex items-center -space-x-1.5">
            {witnesses.slice(0, 4).map((p) => (
              <MiniAvatar key={p.user_id} user={usersById[p.user_id]} size={22} />
            ))}
            {witnesses.length > 4 && (
              <span className="ml-1 text-[10px] text-muted-foreground">+{witnesses.length - 4}</span>
            )}
            {witnesses.length === 0 && <span className="text-xs text-muted-foreground">—</span>}
          </div>
        </td>
      )}
      <td className="py-2.5 px-3">
        <span className="inline-flex items-center gap-1.5 text-xs font-medium">
          <span className="w-2 h-2 rounded-full flex-shrink-0" style={{ background: pri.dot }}></span>
          <span style={{ color: pri.dot }}>{pri.label}</span>
        </span>
      </td>
      <td className="py-2.5 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        {deciders.length === 0 ? (
          <span
            className="inline-flex items-center justify-center rounded-full"
            style={{
              width: 22, height: 22,
              background: "transparent",
              border: "1px dashed rgba(255,255,255,0.10)",
              color: "#6B7280",
            }}
            title="Aucun destinataire"
            data-testid={`avi-read-${it.id}-empty`}
          >
            <EyeOff size={12} strokeWidth={2.4} />
          </span>
        ) : (
          <HoverCard openDelay={200} closeDelay={100}>
            <HoverCardTrigger asChild>
              <span
                className="inline-flex items-center cursor-help"
                data-testid={`avi-read-${it.id}`}
              >
                {deciders.slice(0, 4).map((p, i) => {
                  const seen = decidedSeen(p);
                  const bg = seen ? "rgba(34,197,94,0.20)" : "rgba(107,114,128,0.20)";
                  const color = seen ? "#22C55E" : "#9CA3AF";
                  const IconTag = seen ? Eye : EyeOff;
                  return (
                    <span
                      key={p.user_id}
                      className="rounded-full inline-flex items-center justify-center shrink-0"
                      style={{
                        width: 22, height: 22,
                        marginLeft: i === 0 ? 0 : -6,
                        zIndex: deciders.length - i,
                        background: bg,
                        color,
                        border: "2px solid #141414",
                      }}
                    >
                      <IconTag size={12} strokeWidth={2.4} />
                    </span>
                  );
                })}
                {deciders.length > 4 && (
                  <span
                    className="ml-1 text-[10px] font-semibold"
                    style={{ color: "#9CA3AF" }}
                  >
                    +{deciders.length - 4}
                  </span>
                )}
              </span>
            </HoverCardTrigger>
            <HoverCardContent
              side="top"
              align="center"
              className="p-3 border-0 shadow-xl"
              style={{ background: "#1B1B1B", border: "1px solid #2A2A2A", minWidth: 260 }}
              data-testid={`avi-read-detail-${it.id}`}
            >
              <div className="text-[10px] uppercase tracking-widest text-muted-foreground mb-2">
                {isInfo ? "Lecture" : isAccuse ? "Accusé de réception" : "Décisions"} ({nSeen}/{nTotal})
              </div>
              <ul className="space-y-1.5">
                {deciders.map((p) => {
                  const u = usersById[p.user_id];
                  const seen = decidedSeen(p);
                  const color = seen ? "#22C55E" : "#6B7280";
                  const IconTag = seen ? Eye : EyeOff;
                  let dateStr = null;
                  if (seen && p.decision_at) {
                    try {
                      const d = new Date(p.decision_at);
                      dateStr = `${d.toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "2-digit" })} · ${d.toLocaleTimeString("fr-FR", { hour: "2-digit", minute: "2-digit" })}`;
                    } catch { /* ignore */ }
                  }
                  return (
                    <li
                      key={p.user_id}
                      className="flex items-center gap-2.5 text-[11px]"
                    >
                      <IconTag size={12} strokeWidth={2.4} style={{ color }} className="shrink-0" />
                      <MiniAvatar user={u} size={20} />
                      <span className="flex-1 truncate" style={{ color: "#EDEDED" }}>
                        {u ? `${u.first_name || ""} ${u.last_name || ""}`.trim() || u.email : "—"}
                      </span>
                      <span style={{ color }} className="whitespace-nowrap tabular-nums text-[10.5px]">
                        {seen ? (dateStr || "Vu") : "Non lu"}
                      </span>
                    </li>
                  );
                })}
              </ul>
            </HoverCardContent>
          </HoverCard>
        )}
      </td>
      {/* Spec iter256 (2026-03-01) — Colonne « Clôturing » : compte à
          rebours avant la clôture automatique (feature paramétrable par
          sous-type). Rendue uniquement si activée pour ce sous-type. */}
      {showCloturing ? (
        <td className="py-2.5 px-3 text-center" data-testid={`avi-cloturing-cell-${it.id}`}>
          <ClotutingCountdown dueAt={it._auto_close_at} autoCloseHours={autoCloseHours} />
        </td>
      ) : null}
      {/* Spec iter302 — colonne Activité (pastille + infobulle compacte). */}
      <td className="py-2.5 px-3 text-center whitespace-nowrap overflow-hidden" style={{ minWidth: 84 }} onClick={(e) => e.stopPropagation()} data-testid={`avi-activity-${it.id}`}>
        {(() => {
          const iso = it.activity?.at || it.updated_at || it.created_at;
          if (!iso) return <span className="italic text-[10.5px]" style={{ color: "rgba(255,255,255,0.25)" }}>—</span>;
          return (
            <AviActivityHovercard avi={it} user={usersById[it.activity?.actor_id]}>
              <span className="inline-flex items-center tabular-nums" style={activityPillStyle(iso)}>
                {formatActivityShort(iso)}
              </span>
            </AviActivityHovercard>
          );
        })()}
      </td>
      <td className="py-2.5 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        {(() => {
          const hasUnread = unread > 0;
          const overNine = unread > 9;
          const hasAnyMsg = msgTotal > 0;
          const go = (e) => {
            e.stopPropagation();
            navigate(`/avi/${it.id}?scroll=chat`);
          };
          return (
            <button
              type="button"
              onClick={go}
              title={
                hasUnread
                  ? `${unread} message${unread > 1 ? "s" : ""} non lu${unread > 1 ? "s" : ""}`
                  : hasAnyMsg
                    ? `${msgTotal} message${msgTotal > 1 ? "s" : ""} — tous lus`
                    : "Aucun message — ouvrir la discussion"
              }
              className="relative inline-flex items-center justify-center transition-transform hover:scale-105"
              style={{
                width: 26, height: 26,
                borderRadius: 999,
                background: "rgba(37,211,102,0.14)",
                color: "#25D366",
                border: "1px solid rgba(37,211,102,0.35)",
                cursor: "pointer",
              }}
              data-testid={`avi-discussion-btn-${it.id}`}
            >
              <MessageCircle size={13} strokeWidth={2.6} />
              {hasUnread && (
                <span
                  className="absolute font-bold tabular-nums text-white flex items-center justify-center leading-none"
                  style={{
                    top: -4, right: -4,
                    minWidth: 14, height: 14,
                    padding: overNine ? "0 3px" : 0,
                    fontSize: 8,
                    background: "#EF4444",
                    borderRadius: 999,
                    boxShadow: "0 0 0 2px #1E1E1E",
                  }}
                  data-testid={`avi-discussion-badge-${it.id}`}
                >
                  {overNine ? "9+" : unread}
                </span>
              )}
            </button>
          );
        })()}
      </td>
      <td className="py-2.5 px-3 text-center" onClick={(e) => e.stopPropagation()}>
        <AviAttachmentsPopover avi={it} count={attCount} />
      </td>
      <td className="py-2.5 px-3" onClick={(e) => e.stopPropagation()}>
        <div className="flex items-center justify-center gap-1.5">
          <AviInfoHovercard avi={it} usersById={usersById}>
            <button
              type="button"
              onClick={() => navigate(`/avi/${it.id}`)}
              className="inline-flex items-center justify-center transition-all hover:scale-[1.08] hover:saturate-[1.15]"
              style={{
                width: 30, height: 30, borderRadius: 999,
                background: "rgba(90,158,224,0.14)",
                border: "1px solid rgba(90,158,224,0.38)",
                color: "#5A9EE0",
              }}
              title="Détails (survolez pour aperçu)"
              data-testid={`avi-act-info-${it.id}`}
            >
              <InfoIcon size={14} strokeWidth={2.4} />
            </button>
          </AviInfoHovercard>
          {hasEdit && <button
            type="button"
            onClick={() => canEdit
              ? onEdit(it)
              : toast.info("Impossible d'éditer une demande clôturée")}
            disabled={!canEdit}
            className="inline-flex items-center justify-center transition-all hover:scale-[1.08] hover:saturate-[1.15] disabled:hover:scale-100 disabled:hover:saturate-100"
            style={{
              width: 30, height: 30, borderRadius: 999,
              background: "#262931",
              border: "1px solid rgba(156,163,175,0.28)",
              color: "#9CA3AF",
              opacity: canEdit ? 1 : 0.45,
              cursor: canEdit ? "pointer" : "not-allowed",
            }}
            title={canEdit ? "Édition rapide" : "Non modifiable"}
            data-testid={`avi-act-quick-edit-${it.id}`}
          >
            <Pencil size={14} strokeWidth={2.4} />
          </button>}
          {canDel && <button
            type="button"
            onClick={(e) => onDelete(it, e)}
            disabled={!canDel}
            className="inline-flex items-center justify-center transition-all hover:scale-[1.08] hover:saturate-[1.15] disabled:hover:scale-100 disabled:hover:saturate-100"
            style={{
              width: 30, height: 30, borderRadius: 999,
              background: "rgba(239,68,68,0.14)",
              border: "1px solid rgba(239,68,68,0.38)",
              color: "#EF4444",
              opacity: canDel ? 1 : 0.45,
              cursor: canDel ? "pointer" : "not-allowed",
            }}
            title={canDel ? "Supprimer" : "Réservé aux administrateurs"}
            data-testid={`avi-act-delete-${it.id}`}
          >
            <Trash2 size={14} strokeWidth={2.4} />
          </button>}
        </div>
      </td>
    </tr>
  );
}
